import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core'

import { MessageService } from 'primeng/api'
import { ButtonModule } from 'primeng/button'
import { ToastModule } from 'primeng/toast'
import { interval, Subject, takeUntil } from 'rxjs'
import { debounceTime, filter, switchMap, take } from 'rxjs/operators'

import { RealtimeEventType } from '@ti/contracts/data/realtime'
import { injectDestroy$ } from '@ti/core/common/utils/inject-destroy-event'
import { injectIsPageInForeground$ } from '@ti/core/common/utils/inject-is-bg-fg'
import { AppUpdateInfo, UPDATE_MANAGER } from '@ti/core/contracts/update'
import { RealtimeClient } from '@ti/core/realtime'

import { LanguageService } from '../../../i18n/services/language.service'
import { AppUpdatePromptPolicy } from './app-update-prompt.policy'

@Component({
  selector: 'app-update-notif-host',
  templateUrl: 'app-update.component.html',
  styleUrl: 'app-update.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ToastModule, ButtonModule],
})
export class AppUpdateComponent implements OnInit {
  protected readonly ONE_HOUR_MILLISECONDS = 60 * 60 * 1000

  protected readonly messageService = inject(MessageService)
  protected readonly languageService = inject(LanguageService)
  protected readonly updateManager = inject(UPDATE_MANAGER, { optional: true })
  protected readonly promptPolicy = inject(AppUpdatePromptPolicy)
  protected readonly realtimeClient = inject(RealtimeClient)
  protected readonly isPageInForeground$ = injectIsPageInForeground$()
  protected readonly destroy$ = injectDestroy$()

  // Kept until the page reloads: the service worker reports a downloaded version only once.
  protected pendingServiceWorkerUpdate: AppUpdateInfo | null = null
  protected readonly proposeRequests = new Subject<void>()
  // Set by the TI Admin broadcast; lifts the daily limit until this page reloads.
  protected isUpdateForced = false

  public ngOnInit() {
    this.initUpdateSubscription()
  }

  protected initUpdateSubscription() {
    if (!this.updateManager) {
      console.debug('Update manager not available')
      return
    }

    this.proposeRequests
      .pipe(
        switchMap(() => this.isPageInForeground$.pipe(filter(Boolean), take(1))),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.proposePendingServiceWorkerUpdate())

    this.updateManager.versionReady$
      .pipe(takeUntil(this.destroy$), debounceTime(512))
      .subscribe((versionInfo) => this.onUpdateAvailable(versionInfo))

    // Check for updates every hour
    interval(this.ONE_HOUR_MILLISECONDS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkForUpdate())

    if (this.updateManager.updateType === 'SERVICE_WORKER') {
      this.initForcedUpdateSubscription()
    }
  }

  protected initForcedUpdateSubscription() {
    this.realtimeClient
      .subscribeToEvents(RealtimeEventType.AppUpdateForced, () => {
        this.isUpdateForced = true
        this.checkForUpdate()
      })
      // `complete` also runs when the component was destroyed before the listener settled.
      .then((unsubscribe) => this.destroy$.subscribe({ complete: () => unsubscribe() }))
      .catch((e) => console.error('Cannot subscribe to forced app updates', e))
  }

  protected async checkForUpdate() {
    try {
      const versionInfo = await this.updateManager!.getAppUpdateInfo()
      if (versionInfo.updateAvailability === 'UPDATE_AVAILABLE') {
        console.debug('A new version is available.')
        await this.onUpdateAvailable(versionInfo)
      } else if (this.pendingServiceWorkerUpdate) {
        this.proposeRequests.next()
      }
    } catch (e: unknown) {
      console.error('Failed to check for updates:', e)
    }
  }

  protected async onUpdateAvailable(versionInfo: AppUpdateInfo) {
    if (versionInfo.updateType !== 'SERVICE_WORKER') {
      return this.showUpdateDialog(versionInfo)
    }

    this.pendingServiceWorkerUpdate = versionInfo
    this.proposeRequests.next()
  }

  protected proposePendingServiceWorkerUpdate() {
    const versionInfo = this.pendingServiceWorkerUpdate
    if (!versionInfo) return

    if (!this.isUpdateForced && !this.promptPolicy.canPrompt()) {
      console.debug('An update was already suggested today.')
      return
    }

    this.promptPolicy.markPrompted()
    this.showUpdateDialog(versionInfo)
  }

  protected async showUpdateDialog(versionInfo: AppUpdateInfo) {
    const labels = await this.languageService.massTranslate({
      summary:
        versionInfo.updateType === 'SERVICE_WORKER'
          ? 'app-update.service-worker.new-version'
          : 'app-update.native-store.new-version',
      detail:
        versionInfo.updateType === 'SERVICE_WORKER'
          ? 'app-update.service-worker.install-btn'
          : 'app-update.native-store.view-btn',
    })

    console.debug(`Request user update the app`)
    this.messageService.clear('app-update')
    this.messageService.add({
      ...labels,
      key: 'app-update',
      closable: true,
      life: this.ONE_HOUR_MILLISECONDS,
    })
  }
}
